import { useEffect, useMemo, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BUG_RESOLUTIONS,
  BUG_STATUSES,
  addBugComment,
  formatBugDate,
  getBugActivity,
  getBugById,
  getBugComments,
  updateBug,
} from "../services/bugService";
import { getUsersSummary } from "../services/userService";
import { useAuth } from "../hooks/useAuth";
import { routePaths } from "../constants/routes";
import "../style/bugTracker.css";

const tabs = ["Details", "Comments", "Attachments", "History"];

export default function BugDetail() {
  const { id } = useParams();
  const { user, role } = useAuth();
  const [bug, setBug] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [activeTab, setActiveTab] = useState("Details");
  const [commentText, setCommentText] = useState("");
  const [saving, setSaving] = useState(false);
  const canManage = ["admin", "trainer"].includes(role);

  const editableState = useMemo(() => ({
    status: bug?.status || "Open",
    resolution: bug?.resolution || "",
    assignedToId: bug?.assignedToId || "",
  }), [bug]);
  const [editState, setEditState] = useState(editableState);

  useEffect(() => {
    setEditState(editableState);
  }, [editableState]);

  useEffect(() => {
    let isMounted = true;

    async function loadBug() {
      try {
        const [bugData, commentData, activityData, usersSummary] = await Promise.all([
          getBugById(id),
          getBugComments(id),
          getBugActivity(id),
          getUsersSummary().catch(() => ({ users: [] })),
        ]);

        if (!isMounted) return;
        setBug(bugData);
        setComments(commentData);
        setActivity(activityData);
        setAssignees(
          usersSummary.users
            .filter((candidate) => ["admin", "trainer"].includes(candidate.role))
            .map((candidate) => ({
              id: candidate.id,
              name: candidate.fullName || candidate.displayName || candidate.email || candidate.id,
            }))
        );
      } catch (error) {
        console.error("Unable to load bug detail:", error);
        toast.error("Unable to load bug details.");
      }
    }

    loadBug();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const refreshBug = async () => {
    const [bugData, commentData, activityData] = await Promise.all([
      getBugById(id),
      getBugComments(id),
      getBugActivity(id),
    ]);
    setBug(bugData);
    setComments(commentData);
    setActivity(activityData);
  };

  const handleUpdate = async () => {
    setSaving(true);

    try {
      const selectedAssignee = assignees.find((assignee) => assignee.id === editState.assignedToId);
      await updateBug(
        id,
        {
          status: editState.status,
          resolution: editState.resolution,
          assignedToId: editState.assignedToId,
          assignedToName: selectedAssignee?.name || "",
        },
        user
      );
      await refreshBug();
      toast.success("Bug updated.");
    } catch (error) {
      console.error("Unable to update bug:", error);
      toast.error("Unable to update bug.");
    } finally {
      setSaving(false);
    }
  };

  const handleCommentSubmit = async (event) => {
    event.preventDefault();

    if (!commentText.trim()) return;
    setSaving(true);

    try {
      await addBugComment(id, commentText.trim(), user);
      setCommentText("");
      await refreshBug();
      toast.success("Comment added.");
    } catch (error) {
      console.error("Unable to add comment:", error);
      toast.error("Unable to add comment.");
    } finally {
      setSaving(false);
    }
  };

  if (!bug) {
    return <main className="bug-page"><div className="bug-empty-state">Loading bug details...</div></main>;
  }

  const detailRows = [
    ["Bug ID", bug.bugId],
    ["Project", bug.project || "QA Coder"],
    ["Module", bug.module],
    ["Feature", bug.feature],
    ["Type", bug.type],
    ["Environment", bug.environment],
    ["Browser / Device", bug.browserDevice],
    ["OS", bug.os],
    ["Build Version", bug.buildVersion],
    ["Priority", bug.priority],
    ["Severity", bug.severity],
    ["Status", bug.status],
    ["Resolution", bug.resolution || "-"],
    ["Assignee", bug.assignedToName || "Unassigned"],
    ["Reporter", bug.reportedByName],
    ["Due Date", bug.dueDate || "-"],
    ["Created", formatBugDate(bug.createdAt)],
    ["Updated", formatBugDate(bug.updatedAt)],
  ];

  return (
    <main className="bug-page">
      <section className="bug-hero">
        <div>
          <span className="bug-eyebrow">{bug.bugId}</span>
          <h1>{bug.title}</h1>
          <p>{bug.module || "General"} defect raised by {bug.reportedByName || "QA Coder user"}.</p>
        </div>
        <NavLink className="bug-secondary-action" to={routePaths.bugs}>Back to Bugs</NavLink>
      </section>

      <section className="bug-detail-layout">
        <article className="bug-panel">
          <div className="bug-tabs">
            {tabs.map((tab) => (
              <button type="button" className={activeTab === tab ? "active" : ""} onClick={() => setActiveTab(tab)} key={tab}>
                {tab}
              </button>
            ))}
          </div>

          {activeTab === "Details" && (
            <div className="bug-detail-content">
              <div className="bug-detail-grid">
                {detailRows.map(([label, value]) => (
                  <div key={label}><span>{label}</span><strong>{value || "-"}</strong></div>
                ))}
              </div>
              <div className="bug-description-stack">
                <section><h3>Description</h3><p>{bug.description || "-"}</p></section>
                <section><h3>Precondition</h3><p>{bug.precondition || "-"}</p></section>
                <section><h3>Steps to Reproduce</h3><ol>{(bug.stepsToReproduce || []).map((step) => <li key={step}>{step}</li>)}</ol></section>
                <section><h3>Expected Result</h3><p>{bug.expectedResult || "-"}</p></section>
                <section><h3>Actual Result</h3><p>{bug.actualResult || "-"}</p></section>
                <section><h3>Labels</h3><p>{bug.labels?.length ? bug.labels.join(", ") : "-"}</p></section>
              </div>
            </div>
          )}

          {activeTab === "Comments" && (
            <div className="bug-comments">
              <form onSubmit={handleCommentSubmit}>
                <textarea rows="4" value={commentText} onChange={(event) => setCommentText(event.target.value)} placeholder="Add comment or retest note..." />
                <button type="submit" className="bug-primary-action" disabled={saving}>Add Comment</button>
              </form>
              {comments.map((comment) => (
                <article className="bug-comment-card" key={comment.id}>
                  <strong>{comment.actorName}</strong>
                  <span>{formatBugDate(comment.createdAt)}</span>
                  <p>{comment.comment}</p>
                </article>
              ))}
            </div>
          )}

          {activeTab === "Attachments" && (
            <div className="bug-attachment-grid">
              {bug.attachments?.length ? bug.attachments.map((attachment) => (
                <article key={`${attachment.name}-${attachment.size}`} className="bug-attachment-card">
                  <strong>{attachment.name}</strong>
                  <span>{attachment.category}</span>
                  <small>{Math.ceil((attachment.size || 0) / 1024)} KB</small>
                </article>
              )) : <div className="bug-empty-state">No attachments added.</div>}
            </div>
          )}

          {activeTab === "History" && (
            <div className="bug-timeline">
              {activity.map((item) => (
                <article key={item.id}>
                  <span>{formatBugDate(item.createdAt)}</span>
                  <strong>{item.message}</strong>
                  <small>{item.actorName}</small>
                </article>
              ))}
            </div>
          )}
        </article>

        <aside className="bug-panel bug-workflow-panel">
          <h2>Tracking Fields</h2>
          <label>Status<select value={editState.status} onChange={(event) => setEditState((current) => ({ ...current, status: event.target.value }))}>{BUG_STATUSES.map((status) => <option key={status}>{status}</option>)}</select></label>
          <label>Resolution<select value={editState.resolution} onChange={(event) => setEditState((current) => ({ ...current, resolution: event.target.value }))}>{BUG_RESOLUTIONS.map((resolution) => <option key={resolution} value={resolution}>{resolution || "None"}</option>)}</select></label>
          <label>Assigned To<select value={editState.assignedToId} disabled={!canManage} onChange={(event) => setEditState((current) => ({ ...current, assignedToId: event.target.value }))}><option value="">Unassigned</option>{assignees.map((assignee) => <option value={assignee.id} key={assignee.id}>{assignee.name}</option>)}</select></label>
          <button type="button" className="bug-primary-action" disabled={saving} onClick={handleUpdate}>{saving ? "Updating..." : "Update Status"}</button>
          <div className="bug-workflow-note">
            <strong>Recommended workflow</strong>
            <span>Open -> In Progress -> Resolved -> Retest -> Closed</span>
            <span>Retest -> Reopened when the fix fails.</span>
          </div>
        </aside>
      </section>
    </main>
  );
}
